import { Component } from 'react';
import PropTypes from 'prop-types';
import TabBar from '@alltrails/shared/denali/components/TabBar';
import * as styles from './styles/styles.module.scss';
import TrailAlertModal from '../../components/TrailAlertModal';
import BulkTrailEditTrailsList from './BulkTrailEditTrailsList';
import BulkTrailEditLeftPanel from './BulkTrailEditLeftPanel';
import { ServerCommunicationUtil } from '../../utils/server_communication_util';
import { wrapEventHandler } from '../../utils/handlers';
import CustomProvider from 'components/CustomProvider';

const bulkTabs = [
  { label: 'Tags', key: 'tags' },
  { label: 'Alerts', key: 'alerts' }
];

const mutuallyExclusiveTags = {
  dogs: 'dog-tags',
  'dogs-no': 'dog-tags',
  'dogs-leash': 'dog-tags'
};

class BulkTrailEdit extends Component {
  constructor(props) {
    super(props);
    const { trails } = this.props;
    const { type, alerts } = this.props.location;
    const typeDisplay = type === 'area' ? 'park' : type;
    this.state = {
      activeTab: bulkTabs[0].key,
      typeDisplay,
      trails: trails.sort((a, b) => a.name.localeCompare(b.name)),
      changes: {},
      selectedTrails: this.allTrailsHash(),
      highlightedTag: null,
      applyingChanges: false,
      alerts,
      changesApplied: false,
      showAlertModal: false,
      alertModalAlert: null
    };
  }

  componentDidMount() {
    if (typeof window !== 'undefined') {
      window.addEventListener('beforeunload', e => {
        if (this.countChangesToApply()) {
          e.preventDefault();
          e.returnValue = `Warning: ${this.state.typeDisplay} has not been refreshed since last bulk action.`;
        }
      });
    }
  }

  countChangesToApply() {
    let { changes } = this.state;
    let count = 0;
    let changeCounts = {
      tags: 0,
      alerts: 0
    };
    Object.keys(changes).forEach(trailId => {
      if (changes[trailId].tags) {
        count += changes[trailId].tags.add.length + changes[trailId].tags.sub.length;
        changeCounts['tags'] += changes[trailId].tags.add.length + changes[trailId].tags.sub.length;
      }
      if (changes[trailId].alerts) {
        count += changes[trailId].alerts.add.length + changes[trailId].alerts.sub.length;
        changeCounts['alerts'] += changes[trailId].alerts.add.length + changes[trailId].alerts.sub.length;
      }
    });
    return count > 0 ? changeCounts : null;
  }

  handleTrailSelectChange(trailId) {
    let { selectedTrails } = this.state;
    selectedTrails[trailId] = !selectedTrails[trailId];
    this.setState({ selectedTrails });
  }

  countSelectedTrails() {
    return Object.values(this.state.selectedTrails).filter(Boolean).length;
  }

  addRemoveTrailTag(changes, trail, tagUid, add) {
    const trailId = trail.id;
    const trailHasThisTag = this.trailHasTag(trail, tagUid);
    let tags = changes[trailId] && changes[trailId].tags ? changes[trailId].tags : { add: [], sub: [] };
    if (add && !tags.add.includes(tagUid) && (!trailHasThisTag || tags.sub.includes(tagUid))) {
      if (tags.sub.includes(tagUid)) {
        tags.sub = tags.sub.filter(uid => uid !== tagUid);
      } else {
        tags.add.push(tagUid);
      }
    } else if (!add && !tags.sub.includes(tagUid) && (trailHasThisTag || tags.add.includes(tagUid))) {
      if (tags.add.includes(tagUid)) {
        tags.add = tags.add.filter(uid => uid !== tagUid);
      } else {
        tags.sub.push(tagUid);
      }
    }
    changes[trailId] = Object.assign({}, changes[trailId], { tags });
  }

  tagClick(tag) {
    const highlightedTag = tag === this.state.highlightedTag ? null : tag;
    this.setState({ highlightedTag });
  }

  addRemoveTag(tag, add) {
    let { changes, selectedTrails, trails } = this.state;
    trails.forEach(trail => {
      if (selectedTrails[trail.id]) {
        this.addRemoveTrailTag(changes, trail, tag.uid, add);
        // If adding a tag in a mutually-exclusive set (eg the dog-related tags) then remove any other tags in the set:
        if (add && mutuallyExclusiveTags[tag.uid]) {
          Object.keys(mutuallyExclusiveTags).forEach(otherTagUid => {
            if (otherTagUid !== tag.uid && mutuallyExclusiveTags[otherTagUid] === mutuallyExclusiveTags[tag.uid]) {
              this.addRemoveTrailTag(changes, trail, otherTagUid, false);
            }
          });
        }
      }
    });
    this.setState({ changes, changesApplied: false });
  }

  allTrailsHash() {
    let selectedTrails = {};
    this.props.trails.forEach(trail => (selectedTrails[trail.id] = true));
    return selectedTrails;
  }

  selectAllNone(all) {
    let selectedTrails = all ? this.allTrailsHash() : {};
    this.setState({ selectedTrails });
  }

  trailHasTag(trail, tagUid) {
    return (
      trail.attributes.activities.find(attr => attr.uid === tagUid) ||
      trail.attributes.features.find(attr => attr.uid === tagUid) ||
      trail.attributes.obstacles.find(attr => attr.uid === tagUid)
    );
  }

  applyChanges() {
    if (!confirm('Are you sure you want to submit these changes?')) {
      return;
    }
    this.setState({ applyingChanges: true, changesApplied: false });
    const params = {
      type: this.props.location.type,
      id: this.props.location.id,
      data: {
        action: 'trail-edits',
        changes: this.state.changes
      }
    };
    ServerCommunicationUtil.postApiEndpoint(
      '/api/alltrails/admin/bulk_trail_edit',
      params,
      () => {
        this.mergeChanges();
        this.setState({ applyingChanges: false, changesApplied: true });
        this.queueLocationRefresh();
      },
      () => {},
      () => {}
    );
  }

  queueLocationRefresh() {
    ServerCommunicationUtil.postApiEndpoint(
      '/api/alltrails/locations/refresh_location_cache',
      {
        location_id: this.props.location.id,
        location_type: this.props.location.type,
        refresh_trails: true
      },
      () => {},
      () => {},
      () => {}
    );
  }

  getTagSections() {
    const { trailTags } = this.props;
    let tagSections = {};
    Object.keys(trailTags).forEach(section => {
      trailTags[section].forEach(tag => {
        tagSections[tag.uid] = {
          section,
          name: tag.name
        };
      });
    });
    return tagSections;
  }

  mergeChanges() {
    let { changes, trails, alerts } = this.state;
    const tagSections = this.getTagSections();
    let trailLevelAlertsToConvertToAreaLevelAlerts = [];
    let trailLevelAlertsToDelete = [];
    trails.forEach(trail => {
      if (changes[trail.id]) {
        if (changes[trail.id].tags) {
          changes[trail.id].tags.add.forEach(tagUid => {
            trail.attributes[tagSections[tagUid].section].push({
              uid: tagUid,
              name: tagSections[tagUid].name
            });
          });
          changes[trail.id].tags.sub.forEach(tagUid => {
            trail.attributes[tagSections[tagUid].section] = trail.attributes[tagSections[tagUid].section].filter(tag => tag.uid !== tagUid);
          });
        }

        // If a trail-level alert has been added to another trail, then mark it as
        // now being an AreaAlert as it will have been converted on the server.
        // If a trail-alert has been removed from its trail but not added to another,
        // then remove it from the alerts list.

        if (changes[trail.id].alerts) {
          alerts.forEach(alert => {
            if (alert.type === 'TrailAlert') {
              if (changes[trail.id].alerts.add.includes(alert.id)) {
                trailLevelAlertsToConvertToAreaLevelAlerts.push(alert);
              } else if (changes[trail.id].alerts.sub.includes(alert.id)) {
                trailLevelAlertsToDelete.push(alert);
              }
            }
          });
          trail.alerts = [...trail.alerts, ...changes[trail.id].alerts.add].sort((a, b) => a - b);
          trail.alerts = trail.alerts.filter(id => !changes[trail.id].alerts.sub.includes(id));
        }
      }
    });
    alerts.forEach(alert => {
      if (trailLevelAlertsToConvertToAreaLevelAlerts.includes(alert)) {
        alert.type = 'AreaAlert';
      }
    });
    alerts = alerts.filter(a => a.type === 'AreaAlert' || !trailLevelAlertsToDelete.includes(a));
    this.setState({ changes: {}, trails, alerts });
  }

  handleTrailTagClick(trail, tagUid, wasAdd) {
    let { changes } = this.state;
    this.addRemoveTrailTag(changes, trail, tagUid, !wasAdd);
    this.setState({ changes });
  }

  handleTabChange = activeTab => {
    if (activeTab !== this.state.activeTab) {
      this.setState({ activeTab });
    }
  };

  // For a trail and an alert, four possible states exist:
  // - alert is not (and was not) on trail
  // - alert not in trail.alerts but is in changes.add, meaning it's not on the trail but is marked to be added
  // - alert in trail.alerts, meaning it's on trail and has not been changed
  // - alert in trail.alerts and changes.sub, meaning it's on the trail but is marked for deletion
  addRemoveTrailAlert(changes, trail, alertId, add) {
    const trailId = trail.id;
    const trailHasThisAlert = trail.alerts.find(id => id === alertId);
    let alertChanges = changes[trailId] && changes[trailId].alerts ? changes[trailId].alerts : { add: [], sub: [] };
    if (add && !alertChanges.add.includes(alertId) && (!trailHasThisAlert || alertChanges.sub.includes(alertId))) {
      if (alertChanges.sub.includes(alertId)) {
        alertChanges.sub = alertChanges.sub.filter(id => id !== alertId);
      } else {
        alertChanges.add.push(alertId);
      }
    } else if (!add && !alertChanges.sub.includes(alertId) && (trailHasThisAlert || alertChanges.add.includes(alertId))) {
      if (alertChanges.add.includes(alertId)) {
        alertChanges.add = alertChanges.add.filter(id => id !== alertId);
      } else {
        alertChanges.sub.push(alertId);
      }
    }
    changes[trailId] = Object.assign({}, changes[trailId], { alerts: alertChanges });
  }

  selectAlertsTrails(alert) {
    const trails = this.alertOnTrails(alert);
    let selectedTrails = {};
    trails.forEach(trail => (selectedTrails[trail.id] = true));
    this.setState({ selectedTrails });
  }

  alertOnTrails(alert) {
    let { changes, trails } = this.state;
    let alertTrails = [];
    trails.forEach(trail => {
      const alertChanges = changes[trail.id] ? changes[trail.id].alerts : null;
      if (alertChanges) {
        if (alertChanges.add.includes(alert.id) || (trail.alerts.includes(alert.id) && !alertChanges.sub.includes(alert.id))) {
          alertTrails.push(trail);
        }
      } else {
        if (trail.alerts.includes(alert.id)) {
          alertTrails.push(trail);
        }
      }
    });
    return alertTrails;
  }

  addRemoveAlertOnSelectedTrails(alertId, add) {
    let { changes, selectedTrails, trails } = this.state;

    trails.forEach(trail => {
      if (selectedTrails[trail.id]) {
        this.addRemoveTrailAlert(changes, trail, alertId, add);
      }
    });

    this.setState({ changes, changesApplied: false });
  }

  removeAlertCompletely(alertId) {
    let { alerts, changes, changesApplied, trails } = this.state;
    const alert = alerts.find(a => a.id === alertId);

    trails.forEach(trail => {
      // Remove it both from state.trails and any occurence of it in current state.changes
      trail.alerts = trail.alerts.filter(id => id !== alertId);

      if (changes[trail.id] && changes[trail.id].alerts) {
        let alerts = changes[trail.id].alerts;
        alerts.add = alerts.add.filter(id => id !== alertId);
        alerts.sub = alerts.sub.filter(id => id !== alertId);
      }
    });
    // There may be no changes left to apply?
    if (!changesApplied) {
      changesApplied = !this.countChangesToApply();
    }
    this.setState({ alerts, changes, trails, changesApplied });
  }

  handleTrailAlertClick(trail, alertId, isAdd) {
    let { alerts, changes, trails } = this.state;
    const alert = alerts.find(a => a.id === alertId);

    this.addRemoveTrailAlert(changes, trail, alertId, isAdd);
    if (isAdd) {
      alert.trailIds.push(trail.id);
    } else {
      alert.trailIds = alert.trailIds.filter(trailId => trailId !== trail.id);
    }

    this.setState({ alerts, changes, trails, changesApplied: false });
  }

  addAlert() {
    this.openAlertModal();
  }

  editAlert(alert) {
    this.openAlertModal(alert);
  }

  deleteAlert(alert) {
    if (confirm(`Are you sure you want to delete ${alert.shortName || alert.content}? This cannot be undone.`)) {
      const url = '/api/alltrails/admin/alerts';
      const params = {
        type: this.props.location.type,
        id: this.props.location.id,
        alert: {
          id: alert.id,
          deleted: true
        }
      };
      ServerCommunicationUtil.postApiEndpoint(
        url,
        params,
        data => {
          let { alerts } = this.state;
          alerts = alerts.filter(a => a.id !== alert.id);
          this.setState({ alerts });
          this.removeAlertCompletely(alert.id);
        },
        () => {},
        () => {}
      );
    }
  }

  renderLocation() {
    const { location } = this.props;
    let refreshLink = null;
    let trainingInfo = null;
    const selectedCount = this.countSelectedTrails();
    if (this.countChangesToApply()) {
      if (this.state.applyingChanges) {
        refreshLink = <span>Updating trails...</span>;
      } else {
        refreshLink = <button onClick={wrapEventHandler(() => this.applyChanges())}>Submit</button>;
        if (selectedCount < this.state.trails.length) {
          trainingInfo = <span>All changes will be submitted, not just for selected trails</span>;
        }
      }
    } else if (this.state.changesApplied) {
      refreshLink = <span>Trails updated and location refresh queued</span>;
    }
    const selectAllButton = (
      <button onClick={wrapEventHandler(() => this.selectAllNone(true))} disabled={selectedCount === this.state.trails.length}>
        All
      </button>
    );
    const selectNoneButton = (
      <button onClick={wrapEventHandler(() => this.selectAllNone(false))} disabled={selectedCount === 0}>
        None
      </button>
    );
    return (
      <table className={styles.location}>
        <tbody>
          <tr>
            <th>{this.state.typeDisplay}:</th>
            <td>
              <a href={`/${location.url}`} target="_blank">
                {location.name}
              </a>
            </td>
          </tr>
          <tr>
            <th>Trails:</th>
            <td>{location.trailCount}</td>
          </tr>
          <tr className={styles.buttonRow}>
            <td></td>
            <td>
              {selectAllButton}
              {selectNoneButton}
              {refreshLink}
              {trainingInfo}
            </td>
          </tr>
        </tbody>
      </table>
    );
  }

  renderSlidingTabs() {
    const changeCounts = this.countChangesToApply();
    const tabs = bulkTabs.map(tab => {
      const label = `${tab.label}${changeCounts && changeCounts[tab.key] ? ` (${changeCounts[tab.key]})` : ''}`;
      return {
        id: tab.key,
        label,
        testId: `bulk-trail-edit-tab-${tab.key}`
      };
    });
    return <TabBar activeTab={this.state.activeTab} onChange={this.handleTabChange} tabs={tabs} testId="bulk-trail-edit-tab-bar" />;
  }

  openAlertModal(alert) {
    this.setState({ showAlertModal: true, alertModalAlert: alert });
  }

  closeAlertModal() {
    this.setState({ showAlertModal: false });
  }

  handleAlertModalSubmit(formParams) {
    const url = '/api/alltrails/admin/alerts';
    const params = {
      type: this.props.location.type,
      id: this.props.location.id,
      alert: formParams
    };
    ServerCommunicationUtil.postApiEndpoint(
      url,
      params,
      data => {
        let { alerts } = this.state;
        if (formParams.id) {
          const alert = alerts.find(a => a.id === formParams.id);
          Object.assign(alert, formParams);
        } else {
          const alert = Object.assign(data, { trailIds: [] });
          alerts.push(alert);
        }
        this.setState({ alerts, showAlertModal: false });
      },
      () => {},
      () => {}
    );
  }

  renderAlertModal() {
    return (
      this.state.showAlertModal && (
        <TrailAlertModal
          locale={this.props.locale}
          closeModal={wrapEventHandler(() => this.closeAlertModal())}
          handleSubmit={params => this.handleAlertModalSubmit(params)}
          showAreaFields={true}
          alert={this.state.alertModalAlert}
        />
      )
    );
  }

  render() {
    const activeIndex = bulkTabs.findIndex(tab => tab.key === this.state.activeTab);
    return (
      <CustomProvider>
        <div className={`${styles.body} xlate-none`}>
          <div className={styles.locationPanel}>{this.renderLocation()}</div>
          {this.renderSlidingTabs()}
          <div className={styles.content}>
            <div className={styles.leftPanel}>
              <BulkTrailEditLeftPanel
                addAlert={() => this.addAlert()}
                activeIndex={activeIndex}
                addRemoveAlertOnSelectedTrails={(alertId, add) => this.addRemoveAlertOnSelectedTrails(alertId, add)}
                addRemoveTag={(tag, add) => this.addRemoveTag(tag, add)}
                alerts={this.state.alerts}
                alertOnTrails={alert => this.alertOnTrails(alert)}
                changes={this.state.changes}
                deleteAlert={alert => this.deleteAlert(alert)}
                editAlert={alert => this.editAlert(alert)}
                highlightedTag={this.state.highlightedTag}
                selectAlertsTrails={alert => this.selectAlertsTrails(alert)}
                selectedTrails={this.state.selectedTrails}
                tabs={bulkTabs}
                tagClick={tag => this.tagClick(tag)}
                trailHasTag={(trail, tagUid) => this.trailHasTag(trail, tagUid)}
                trails={this.state.trails}
                trailTags={this.props.trailTags}
              />
            </div>
            <div className={styles.trailsPanel}>
              <BulkTrailEditTrailsList
                alerts={this.state.alerts}
                activeIndex={activeIndex}
                changes={this.state.changes}
                handleTrailAlertClick={(trail, alertId, isAdd) => this.handleTrailAlertClick(trail, alertId, isAdd)}
                handleTrailSelectChange={trailId => this.handleTrailSelectChange(trailId)}
                handleTrailTagClick={(trail, tagUid, wasAdd) => this.handleTrailTagClick(trail, tagUid, wasAdd)}
                highlightedTag={this.state.highlightedTag}
                selectedTrails={this.state.selectedTrails}
                tabs={bulkTabs}
                trailHasTag={(trail, tagUid) => this.trailHasTag(trail, tagUid)}
                trails={this.state.trails}
              />
            </div>
          </div>
          {this.renderAlertModal()}
        </div>
      </CustomProvider>
    );
  }
}

const tagPropShape = PropTypes.arrayOf(
  PropTypes.shape({
    name: PropTypes.string,
    uid: PropTypes.string.isRequired
  })
).isRequired;

const attributeSectionsShape = PropTypes.shape({
  activities: tagPropShape,
  features: tagPropShape,
  obstacles: tagPropShape
});

BulkTrailEdit.propTypes = {
  locale: PropTypes.string,
  location: PropTypes.shape({
    type: PropTypes.string.isRequired,
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    trailCount: PropTypes.number.isRequired
  }).isRequired,
  alerts: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      shortName: PropTypes.string,
      content: PropTypes.string,
      trailIds: PropTypes.arrayOf(PropTypes.number).isRequired
    })
  ),
  trails: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      slug: PropTypes.string,
      name: PropTypes.string,
      attributes: attributeSectionsShape.isRequired,
      alerts: PropTypes.arrayOf(PropTypes.number).isRequired
    })
  ).isRequired,
  trailTags: attributeSectionsShape
};

export default BulkTrailEdit;
